import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import { toast } from 'react-hot-toast';
import {
  X,
  FlaskConical,
  Truck,
  Package,
  Building2,
  CheckCircle2,
  Star,
  ShieldCheck,
  RotateCcw,
  Ban,
  Archive,
  ArrowRight,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useHasPermission } from '@/shared/permissions/useHasPermission';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { LostReasonModal } from '@/features/deals/components/LostReasonModal';
import { useSampleMutations, useSampleRequest } from '../api';
import { SampleDispatchModal } from './SampleDispatchModal';
import { SampleFeedbackDialog } from './SampleFeedbackDialog';
import { SampleTechnicalApprovalModal } from './SampleTechnicalApprovalModal';
import { FollowupPanel } from '@/features/activity/components/TimelinePanel';
import type { SampleState } from '../types';

interface SampleDetailDrawerProps {
  sampleId: string | null;
  open: boolean;
  onClose: () => void;
}

const LIFECYCLE_STEPS: Array<{ key: SampleState; label: string }> = [
  { key: 'draft', label: '1. Draft' },
  { key: 'dispatched', label: '2. Dispatched' },
  { key: 'delivered', label: '3. Delivered' },
  { key: 'feedback_received', label: '4. Feedback' },
  { key: 'technical_review', label: '5. Tech Sign-Off' },
  { key: 'converted', label: '6. Quotation' },
];

export function SampleDetailDrawer({ sampleId, open, onClose }: SampleDetailDrawerProps) {
  const navigate = useNavigate();
  const { data: sample, isLoading } = useSampleRequest(open ? sampleId : null);
  // The dispatch note is written by whoever dispatches the sample, for
  // Dispatch's own internal use — client-requested: visible to Dispatch only,
  // never printed on the sample document. Gated on the same permission that
  // lets someone open the Dispatch modal in the first place.
  const canSeeDispatchNote = useHasPermission('sampling.dispatch');
  const {
    markDelivered,
    submitTechnicalReview,
    convertToQuotation,
    cancelSample,
    reopenSample,
  } = useSampleMutations();

  // Modals state
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [isRedispatch, setIsRedispatch] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showTechnicalModal, setShowTechnicalModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);

  const qc = useQueryClient();
  const markDealLost = useMutation({
    mutationFn: async (payload: { lostReasonId: string; lostReasonNote: string }) =>
      (await api.post(`/api/sampling/${sample?._id}/mark-deal-lost`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sampling'] });
      qc.invalidateQueries({ queryKey: ['deal'] });
      qc.invalidateQueries({ queryKey: ['deals'] });
      setShowLostModal(false);
      toast.success(`Sample ${sample?.code} and linked deal marked as Closed Lost.`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to mark as lost.'),
  });

  if (!open) return null;

  const currentStepIndex = sample
    ? LIFECYCLE_STEPS.findIndex((s) => s.key === sample.status)
    : 0;

  const handleAction = (action: string) => {
    if (!sample) return;

    switch (action) {
      case 'to_dispatch':
        setIsRedispatch(false);
        setShowDispatchModal(true);
        break;
      case 'redispatch':
        setIsRedispatch(true);
        setShowDispatchModal(true);
        break;
      case 'mark_delivered':
        markDelivered.mutate(sample._id, {
          onSuccess: () => toast.success(`Sample ${sample.code} marked as Delivered.`),
          onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to update.'),
        });
        break;
      case 'record_feedback':
        setShowFeedbackModal(true);
        break;
      case 'submit_for_technical_review':
        submitTechnicalReview.mutate(sample._id, {
          onSuccess: () => toast.success(`Sample ${sample.code} submitted for technical review.`),
          onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to update.'),
        });
        break;
      case 'technical_approve':
        setShowTechnicalModal(true);
        break;
      case 'reject_technical':
        setShowTechnicalModal(true);
        break;
      case 'convert_to_quotation':
        convertToQuotation.mutate(
          { id: sample._id },
          {
            onSuccess: () => {
              toast.success(`Sample ${sample.code} marked as converted — fill in the quotation below.`);
              // sample.lines have no rate/quantity yet — this is a hand-off
              // point, not a complete quotation — so send the user straight
              // into a create form rather than the bare quotations list.
              //
              // Leads and Deals are one pipeline record now, so dealId/leadId
              // are the same kind of id — either resolves the record. Prefer
              // the field-based QuotationFormPage: it reads the client's
              // address/GSTIN/place-of-supply straight from the linked
              // Contact and gets the GST split right, which the Studio (typed
              // name/phone/email/address, one flat tax rate) cannot. The
              // Studio stays the fallback for a sample with no pipeline
              // record behind it — QuotationFormPage has nothing to prefill
              // from without one.
              const dealId = typeof sample.dealId === 'object' ? sample.dealId?._id : sample.dealId;
              const leadId = typeof sample.leadId === 'object' ? sample.leadId?._id : sample.leadId;
              const pipelineRecordId = dealId || leadId;
              if (pipelineRecordId) {
                navigate(`/quotations/new?dealId=${encodeURIComponent(pipelineRecordId)}`);
              } else {
                const params = new URLSearchParams({ type: 'quotation', reference: `Sample ${sample.code}` });
                if (sample.customerName) params.set('name', sample.customerName);
                if (sample.phone) params.set('phone', sample.phone);
                if (sample.email) params.set('email', sample.email);
                if (sample.shippingAddress) params.set('address', sample.shippingAddress);
                navigate(`/documents/create?${params.toString()}`);
              }
              onClose();
            },
            onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to convert.'),
          },
        );
        break;
      case 'close': {
        setShowLostModal(true);
        break;
      }
      case 'cancel': {
        const reason = window.prompt('Reason for cancellation (optional):');
        cancelSample.mutate(
          { id: sample._id, reason: reason || undefined },
          {
            onSuccess: () => toast.success(`Sample ${sample.code} cancelled.`),
            onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to cancel.'),
          },
        );
        break;
      }
      case 'reopen':
        reopenSample.mutate(sample._id, {
          onSuccess: () => toast.success(`Sample ${sample.code} reopened.`),
          onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to reopen.'),
        });
        break;
      default:
        toast.error(`Unhandled action: ${action}`);
    }
  };

  const getStatusBadge = (status: SampleState) => {
    switch (status) {
      case 'draft':
        return <Badge variant="neutral">Draft</Badge>;
      case 'dispatched':
        return <Badge variant="info">In Transit</Badge>;
      case 'delivered':
        return <Badge variant="info">Delivered</Badge>;
      case 'feedback_received':
        return <Badge variant="warning">Feedback Received</Badge>;
      case 'technical_review':
        return <Badge className="bg-primary-container text-primary border-primary/30">Tech Review</Badge>;
      case 'converted':
        return <Badge variant="success">Converted to Quotation</Badge>;
      case 'closed_lost':
        return <Badge variant="danger">Closed (Lost)</Badge>;
      case 'cancelled':
        return <Badge variant="neutral">Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-hidden bg-slate-900/40 backdrop-blur-sm transition-opacity">
        <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
          <div className="w-screen max-w-2xl bg-card shadow-2xl flex flex-col">
            {/* Drawer Header */}
            <div className="p-5 border-b border-border flex items-center justify-between bg-muted/70">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-info/10 text-info-text">
                  <FlaskConical className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-on-surface font-mono">
                      {sample?.code || 'Sample Request'}
                    </h3>
                    {sample && getStatusBadge(sample.status)}
                  </div>
                  <p className="text-xs text-on-surface-variant mt-0.5">
                    Created {sample?.createdAt ? new Date(sample.createdAt).toLocaleDateString() : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {sample && !['converted', 'cancelled', 'closed_lost'].includes(sample.status) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs h-8 text-danger-text hover:bg-danger/10"
                    onClick={() => setShowLostModal(true)}
                    title="Mark sample and linked deal as Closed Lost"
                  >
                    <Archive className="w-3.5 h-3.5 mr-1" /> Mark Lost
                  </Button>
                )}
                {sample?.status === 'draft' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="text-xs h-8"
                    onClick={() => navigate(ROUTES.samplingEdit.replace(':id', String(sampleId)))}
                  >
                    Edit Draft
                  </Button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 rounded-lg text-on-surface-subtle hover:text-on-surface-variant hover:bg-muted transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Drawer Body */}
            {isLoading || !sample ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <Spinner className="h-8 w-8" />
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Lifecycle Progress Stepper */}
                <div className="p-4 rounded-xl bg-muted border border-border">
                  <div className="text-data-xs font-bold text-on-surface-variant uppercase tracking-wider mb-3">
                    Sample Lifecycle
                  </div>
                  <div className="grid grid-cols-6 gap-1.5 text-center">
                    {LIFECYCLE_STEPS.map((step, idx) => {
                      const isCompleted =
                        sample.status === 'converted'
                          ? true
                          : currentStepIndex > idx;
                      const isCurrent = sample.status === step.key;

                      return (
                        <div key={step.key} className="flex flex-col items-center">
                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-data-xs font-bold transition-all ${
                              isCompleted
                                ? 'bg-success text-white'
                                : isCurrent
                                ? 'bg-primary text-white ring-4 ring-primary/30'
                                : 'bg-slate-200 text-on-surface-variant'
                            }`}
                          >
                            {isCompleted ? <CheckCircle2 className="w-3.5 h-3.5" /> : idx + 1}
                          </div>
                          <span
                            className={`text-data-xs mt-1.5 font-semibold leading-tight line-clamp-1 ${
                              isCurrent ? 'text-info-text' : isCompleted ? 'text-success-text' : 'text-on-surface-subtle'
                            }`}
                          >
                            {step.label.replace(/^\d+\.\s*/, '')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {sample.status === 'closed_lost' && (
                  <div className="p-4 rounded-xl border border-danger/30 bg-danger/10/70 space-y-1">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-danger-text uppercase tracking-wider">
                      <Archive className="w-3.5 h-3.5 text-danger-text" /> Sample & Linked Deal Marked as Closed Lost
                    </div>
                    {sample.closedReason && (
                      <p className="text-xs text-danger-text font-medium">
                        <strong>Reason:</strong> {sample.closedReason}
                      </p>
                    )}
                  </div>
                )}

                {/* Customer & Pipeline Anchor Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl border border-border bg-card space-y-2">
                    <div className="text-xs font-bold text-on-surface uppercase tracking-wider flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-info-text" /> Customer Details
                    </div>
                    <div className="text-sm font-bold text-on-surface">{sample.customerName}</div>
                    {sample.phone && (
                      <div className="text-xs text-on-surface-variant flex items-center gap-1.5">
                        <Phone className="w-3 h-3 text-on-surface-subtle" /> {sample.phone}
                      </div>
                    )}
                    {sample.email && (
                      <div className="text-xs text-on-surface-variant flex items-center gap-1.5">
                        <Mail className="w-3 h-3 text-on-surface-subtle" /> {sample.email}
                      </div>
                    )}
                    {sample.shippingAddress && (
                      <div className="text-xs text-on-surface-variant flex items-start gap-1.5 pt-1 border-t border-border">
                        <MapPin className="w-3 h-3 text-on-surface-subtle mt-0.5" />
                        <span>{sample.shippingAddress}</span>
                      </div>
                    )}
                  </div>

                  <div className="p-4 rounded-xl border border-border bg-card space-y-2">
                    <div className="text-xs font-bold text-on-surface uppercase tracking-wider flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-info-text" /> Pipeline Association
                    </div>
                    {sample.dealId ? (
                      <div>
                        <div className="text-xs font-medium text-on-surface-variant">Linked Deal:</div>
                        <div className="text-xs font-bold text-info-text">
                          {typeof sample.dealId === 'object' ? sample.dealId.title : 'Active Deal'}
                        </div>
                      </div>
                    ) : sample.leadId ? (
                      <div>
                        <div className="text-xs font-medium text-on-surface-variant">Linked Lead:</div>
                        <div className="text-xs font-bold text-info-text">
                          {typeof sample.leadId === 'object' ? sample.leadId.name : 'Prospective Lead'}
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-on-surface-subtle">Direct Sample Request</div>
                    )}
                    <div className="pt-2 border-t border-border flex items-center justify-between text-xs">
                      <span className="text-on-surface-variant">Assigned Rep:</span>
                      <span className="font-semibold text-on-surface">
                        {typeof sample.assignedTo === 'object'
                          ? sample.assignedTo?.displayName || sample.assignedTo?.username
                          : 'Unassigned'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Sample Product Lines */}
                <div className="rounded-xl border border-border overflow-hidden bg-card">
                  <div className="p-3.5 bg-muted border-b border-border flex items-center justify-between">
                    <h4 className="text-xs font-bold text-on-surface uppercase tracking-wider flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5 text-info-text" /> Sample Lines ({sample.lines?.length || 0})
                    </h4>
                  </div>
                  <table className="w-full text-left text-xs">
                    <thead className="bg-muted text-on-surface-variant font-semibold border-b border-border">
                      <tr>
                        <th className="p-3">Product</th>
                        <th className="p-3">Pack Variant</th>
                        <th className="p-3 text-right">Quantity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {sample.lines?.map((line, idx) => (
                        <tr key={idx} className="hover:bg-muted/50">
                          <td className="p-3 font-semibold text-on-surface">
                            {line.productName || 'Lubricant Product'}
                            {line.sku && (
                              <span className="block text-data-xs font-mono text-on-surface-subtle">{line.sku}</span>
                            )}
                            {line.notes && (
                              <span className="block text-data-xs text-on-surface-variant italic mt-0.5">
                                Note: {line.notes}
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            <span className="font-medium px-2 py-0.5 rounded bg-muted text-on-surface-variant">
                              {line.packSize}
                            </span>
                          </td>
                          <td className="p-3 text-right font-bold text-on-surface">{line.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Dispatch & Courier Information */}
                {sample.dispatch && (
                  <div className="p-4 rounded-xl border border-info/30 bg-info/10/40 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold text-info-text uppercase tracking-wider flex items-center gap-1.5">
                        <Truck className="w-3.5 h-3.5 text-info-text" /> Dispatch Details
                      </div>
                      {sample.dispatch.deliveredAt ? (
                        <Badge variant="success" className="text-data-xs">
                          Delivered {new Date(sample.dispatch.deliveredAt).toLocaleDateString()}
                        </Badge>
                      ) : (
                        <Badge variant="info" className="text-data-xs">
                          In Transit
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                      <div>
                        <span className="text-on-surface-variant">Courier:</span>{' '}
                        <span className="font-semibold text-on-surface">{sample.dispatch.courier || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-on-surface-variant">AWB / Tracking:</span>{' '}
                        <span className="font-mono font-bold text-on-surface">
                          {sample.dispatch.awb || 'N/A'}
                        </span>
                      </div>
                      {sample.dispatch.expectedDelivery && (
                        <div>
                          <span className="text-on-surface-variant">Expected Delivery:</span>{' '}
                          <span className="font-medium text-on-surface">
                            {sample.dispatch.expectedDelivery}
                          </span>
                        </div>
                      )}
                      {sample.dispatch.dispatchedAt && (
                        <div>
                          <span className="text-on-surface-variant">Dispatched On:</span>{' '}
                          <span className="font-medium text-on-surface">
                            {new Date(sample.dispatch.dispatchedAt).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {(sample.sampleCostAmount ?? 0) > 0 && (
                        <div>
                          <span className="text-on-surface-variant">Cost of goods given:</span>{' '}
                          <span className="font-semibold text-on-surface">₹{sample.sampleCostAmount!.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                    </div>
                    {canSeeDispatchNote && sample.dispatch.notes && (
                      <div className="text-xs text-info-text/80 pt-1 italic">
                        Notes (Dispatch only): {sample.dispatch.notes}
                      </div>
                    )}
                  </div>
                )}

                {/* Customer Trial Feedback */}
                {sample.feedback && (
                  <div
                    className={`p-4 rounded-xl border space-y-2 ${
                      sample.feedback.outcome === 'approved'
                        ? 'bg-success/10/50 border-success/30'
                        : sample.feedback.outcome === 'needs_changes'
                        ? 'bg-warning/10/50 border-warning/30'
                        : 'bg-destructive/10/50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold text-on-surface uppercase tracking-wider flex items-center gap-1.5">
                        <Star className="w-3.5 h-3.5 text-warning-text" /> Customer Trial Verdict
                      </div>
                      <Badge
                        variant={
                          sample.feedback.outcome === 'approved'
                            ? 'success'
                            : sample.feedback.outcome === 'needs_changes'
                            ? 'warning'
                            : 'danger'
                        }
                        className="capitalize"
                      >
                        {sample.feedback.outcome.replace(/_/g, ' ')}
                      </Badge>
                    </div>



                    {sample.feedback.notes && (
                      <p className="text-xs text-on-surface-variant bg-card/80 p-2.5 rounded-lg border border-border">
                        "{sample.feedback.notes}"
                      </p>
                    )}

                    {sample.feedback.attachments && sample.feedback.attachments.length > 0 && (
                      <div className="pt-1">
                        <span className="text-data-xs font-bold uppercase text-on-surface-variant block mb-1.5">
                          Trial Run Documents & Test Reports:
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {sample.feedback.attachments.map((att: any, idx: number) => {
                            const url = typeof att === 'string' ? att : att?.url;
                            return (
                              <a
                                key={idx}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 rounded-lg bg-card border border-border px-2.5 py-1 text-xs font-semibold text-primary hover:bg-primary-container hover:border-indigo-200 shadow-2xs"
                              >
                                <FileText className="w-3.5 h-3.5 text-primary" />
                                <span>Report #{idx + 1}</span>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Technical Review & Lab Sign-Off */}
                {sample.technicalApproval && (
                  <div className="p-4 rounded-xl border border-primary/30 bg-primary-container/50 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold text-primary uppercase tracking-wider flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-primary" /> Technical Sign-Off
                      </div>
                      <Badge className="bg-primary-container text-primary border-primary/30 capitalize">
                        {sample.technicalApproval.decision}
                      </Badge>
                    </div>
                    {sample.technicalApproval.testReportRef && (
                      <div className="text-xs">
                        <span className="text-on-surface-variant">QA Report Ref:</span>{' '}
                        <span className="font-mono font-bold text-primary">
                          {sample.technicalApproval.testReportRef}
                        </span>
                      </div>
                    )}
                    {sample.technicalApproval.notes && (
                      <p className="text-xs text-primary bg-card/80 p-2.5 rounded-lg border border-primary/30">
                        "{sample.technicalApproval.notes}"
                      </p>
                    )}
                  </div>
                )}

                {/* Dispatch & Feedback History Accordion/Log */}
                {sample.dispatchHistory && sample.dispatchHistory.length > 0 && (
                  <div className="p-4 rounded-xl border border-border bg-muted space-y-2">
                    <div className="text-xs font-bold text-on-surface-variant uppercase tracking-wider flex items-center gap-1">
                      <RotateCcw className="w-3 h-3 text-on-surface-variant" /> Previous Dispatch Attempts (
                      {sample.dispatchHistory.length})
                    </div>
                    <div className="space-y-1.5">
                      {sample.dispatchHistory.map((d, i) => (
                        <div
                          key={i}
                          className="text-data-xs p-2 bg-card rounded-md border border-border flex justify-between"
                        >
                          <span>
                            Attempt #{i + 1}: Courier {d.courier || 'N/A'} (AWB: {d.awb || 'N/A'})
                          </span>
                          <span className="text-on-surface-subtle">
                            {d.dispatchedAt ? new Date(d.dispatchedAt).toLocaleDateString() : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* A sample is not complete just because it was dispatched.
                    Keep every client call, response and next date on the
                    request itself so it remains visible until a quotation is
                    raised or the outcome is recorded. */}
                <FollowupPanel
                  parentType="sample"
                  parentId={sample._id}
                  className="bg-card"
                />
              </div>
            )}

            {/* Dynamic Action Buttons Bar from availableActions */}
            {sample && sample.availableActions && sample.availableActions.length > 0 && (
              <div className="p-4 border-t border-border bg-muted flex items-center justify-between flex-wrap gap-2">
                {(sample.availableActions.includes('submit_for_technical_review') ||
                  sample.availableActions.includes('technical_approve')) && (
                  <div className="w-full mb-1 text-data-xs text-primary bg-primary-container/70 border border-primary/30 rounded-lg px-2.5 py-1.5">
                    <strong>Why this step exists:</strong> the customer already approved the trial — this is the
                    internal QC/lab gate that confirms the sampled formulation actually met spec (viscosity,
                    flash point, additive package) before a real quotation is sent. Reject here if the lab
                    result doesn't hold up, even if the customer was happy with it.
                  </div>
                )}
                <div className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                  Available Actions:
                </div>
                {/* Room on the right on phones, so the floating AI Copilot button never covers an action. */}
                <div className="flex items-center gap-2 flex-wrap pr-16 sm:pr-0">
                  {sample.availableActions.map((action) => {
                    if (action === 'to_dispatch') {
                      return (
                        <Button
                          key={action}
                          size="sm"
                          className="gap-1.5 text-xs bg-info hover:bg-info/90 text-white"
                          onClick={() => handleAction('to_dispatch')}
                        >
                          <Truck className="w-3.5 h-3.5" /> To Dispatch
                        </Button>
                      );
                    }
                    if (action === 'dispatch') {
                      // The physical handoff — Dispatch team only, from the
                      // unified Pending Dispatch queue, not from here.
                      return (
                        <span key={action} className="text-data-xs text-on-surface-variant italic">
                          Awaiting Dispatch team
                        </span>
                      );
                    }
                    if (action === 'mark_delivered') {
                      return (
                        <Button
                          key={action}
                          size="sm"
                          variant="secondary"
                          className="gap-1.5 text-xs bg-success/10 text-success-text border-success/30 hover:bg-success/10"
                          onClick={() => handleAction('mark_delivered')}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Mark Delivered
                        </Button>
                      );
                    }
                    if (action === 'record_feedback') {
                      return (
                        <Button
                          key={action}
                          size="sm"
                          className="gap-1.5 text-xs bg-warning hover:bg-warning/90 text-white"
                          onClick={() => handleAction('record_feedback')}
                        >
                          <Star className="w-3.5 h-3.5" /> Record Feedback
                        </Button>
                      );
                    }
                    if (action === 'submit_for_technical_review') {
                      return (
                        <Button
                          key={action}
                          size="sm"
                          className="gap-1.5 text-xs bg-primary hover:bg-primary/90 text-white"
                          onClick={() => handleAction('submit_for_technical_review')}
                        >
                          <ShieldCheck className="w-3.5 h-3.5" /> Submit to Tech Review
                        </Button>
                      );
                    }
                    if (action === 'technical_approve') {
                      return (
                        <Button
                          key={action}
                          size="sm"
                          className="gap-1.5 text-xs bg-primary hover:bg-primary/90 text-white"
                          onClick={() => handleAction('technical_approve')}
                        >
                          <ShieldCheck className="w-3.5 h-3.5" /> Tech Sign-Off
                        </Button>
                      );
                    }
                    if (action === 'convert_to_quotation') {
                      return (
                        <Button
                          key={action}
                          size="sm"
                          className="gap-1.5 text-xs bg-success hover:bg-success/90 text-white"
                          onClick={() => handleAction('convert_to_quotation')}
                        >
                          <ArrowRight className="w-3.5 h-3.5" /> Convert to Quotation
                        </Button>
                      );
                    }
                    if (action === 'redispatch') {
                      return (
                        <Button
                          key={action}
                          size="sm"
                          variant="secondary"
                          className="gap-1.5 text-xs"
                          onClick={() => handleAction('redispatch')}
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Re-dispatch Variant
                        </Button>
                      );
                    }
                    if (action === 'close') {
                      return (
                        <Button
                          key={action}
                          size="sm"
                          variant="ghost"
                          className="gap-1.5 text-xs text-destructive hover:bg-destructive/10"
                          onClick={() => handleAction('close')}
                        >
                          <Archive className="w-3.5 h-3.5" /> Close (Lost)
                        </Button>
                      );
                    }
                    if (action === 'cancel') {
                      return (
                        <Button
                          key={action}
                          size="sm"
                          variant="ghost"
                          className="gap-1.5 text-xs text-on-surface-variant hover:bg-muted"
                          onClick={() => handleAction('cancel')}
                        >
                          <Ban className="w-3.5 h-3.5" /> Cancel
                        </Button>
                      );
                    }
                    if (action === 'reopen') {
                      return (
                        <Button
                          key={action}
                          size="sm"
                          variant="secondary"
                          className="gap-1.5 text-xs"
                          onClick={() => handleAction('reopen')}
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Reopen Sample
                        </Button>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sub-modals */}
      {showDispatchModal && (
        <SampleDispatchModal
          open={showDispatchModal}
          onClose={() => setShowDispatchModal(false)}
          sample={sample || null}
          isRedispatch={isRedispatch}
        />
      )}

      {showFeedbackModal && (
        <SampleFeedbackDialog
          open={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          sample={sample || null}
        />
      )}

      {showTechnicalModal && (
        <SampleTechnicalApprovalModal
          open={showTechnicalModal}
          onClose={() => setShowTechnicalModal(false)}
          sample={sample || null}
        />
      )}

      {showLostModal && sample && (
        <LostReasonModal
          deal={{
            title: (typeof sample.dealId === 'object' ? sample.dealId?.title : null) || sample.customerName || sample.code,
          } as any}
          onCancel={() => setShowLostModal(false)}
          onConfirm={(payload) => markDealLost.mutate(payload)}
          isSaving={markDealLost.isPending}
        />
      )}
    </>
  );
}
